import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { apiErrorMessage } from "@/lib/api-error";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { packagePolicySchema, packageQuantities, type PackagePolicy } from "@shared/credit-packages";

const configKey = ["/api/creditos/pacotes/configuracao"];
export function PacotesCreditos() {
  const { toast } = useToast();
  const query = useQuery<PackagePolicy>({ queryKey: configKey, queryFn: async () =>
    (await apiRequest("GET", configKey[0])).json(),
  });
  const [draft, setDraft] = useState<PackagePolicy | null>(null);
  const policy = draft ?? query.data;
  const save = useMutation({
    mutationFn: async (value: PackagePolicy) => (await apiRequest("PUT", configKey[0], value)).json() as Promise<PackagePolicy>,
    onSuccess: (value) => {
      queryClient.setQueryData(configKey, value);
      queryClient.invalidateQueries({ queryKey: ["/api/creditos/pacotes"] });
      setDraft(null);
      toast({ title: "Pacotes atualizados", description: "As regras passam a valer para os parceiros da sua vistoriadora." });
    },
    onError: (error) => toast({ title: "Não foi possível salvar", description: apiErrorMessage(error), variant: "destructive" }),
  });
  const valid = policy && packagePolicySchema.safeParse(policy);
  const update = (patch: Partial<PackagePolicy>) => policy && setDraft({ ...policy, ...patch });
  return <Card>
    <CardHeader>
      <CardTitle className="text-base">Pacotes de créditos</CardTitle>
      <CardDescription>Defina como seus parceiros escolhem o total de créditos da compra.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-5">
      {query.isLoading ? <p className="text-sm text-muted-foreground" role="status">Carregando regras de compra...</p> : query.isError ?
        <div role="alert"><p className="text-sm">Não foi possível carregar os pacotes.</p><Button variant="outline" onClick={() => query.refetch()}>Tentar novamente</Button></div> : policy && <>
        <div className="flex items-center justify-between gap-4">
          <div><Label htmlFor="pacotes-livre">Quantidade livre</Label><p className="text-sm text-muted-foreground">Mantém a compra habitual, com quantidades escolhidas por tipo de veículo, junto dos pacotes.</p></div>
          <Switch id="pacotes-livre" checked={policy.allowCustom} disabled={save.isPending} onCheckedChange={(allowCustom) => update({ allowCustom })} />
        </div>
        <div className="flex items-center justify-between gap-4">
          <div><Label htmlFor="pacotes-passo">Compra em incrementos</Label><p className="text-sm text-muted-foreground">Oferece pacotes e ajustes pelo passo configurado.</p></div>
          <Switch id="pacotes-passo" checked={policy.stepEnabled} disabled={save.isPending} onCheckedChange={(stepEnabled) => update({ stepEnabled })} />
        </div>
        <div className="max-w-xs space-y-2">
          <Label htmlFor="pacotes-incremento">De quantos em quantos créditos?</Label>
          <Input id="pacotes-incremento" type="number" min={1} max={199} step={1} value={Number.isFinite(policy.step) ? policy.step : ""} disabled={!policy.stepEnabled || save.isPending}
            onChange={(e) => update({ step: e.target.value === "" ? NaN : Number(e.target.value) })} />
          <p className="text-xs text-muted-foreground">De 1 a 199, para oferecer pelo menos cinco pacotes até 999 créditos.</p>
        </div>
        <p className="text-sm text-muted-foreground">{!policy.stepEnabled ? "Sem pacotes, a compra normal por tipo de veículo permanece disponível, mesmo com as duas opções desligadas." : policy.allowCustom ? "O parceiro pode escolher entre a compra normal por veículo e os pacotes com triagem." : `O parceiro compra por pacote, em múltiplos de ${policy.step || "um passo válido"}, distribuídos na triagem.`}</p>
        {valid?.success && policy.stepEnabled && <p className="text-sm">Pacotes iniciais: <strong className="tabular-nums">{packageQuantities(policy).join(", ")}</strong> créditos.</p>}
        {valid && !valid.success && <p role="alert" className="text-sm text-destructive">{valid.error.issues[0].message}</p>}
        <Button disabled={!draft || !valid?.success || save.isPending} onClick={() => save.mutate(policy)}>
          {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar pacotes
        </Button>
      </>}
    </CardContent>
  </Card>;
}
